import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from '../shopify/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthStatus({ shop, onAuthStatusChange }) {
  const [authStatus, setAuthStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const checkAuthStatus = useCallback(async () => {
    if (!shop) return;

    try {
      setLoading(true);
      setError('');
      
      
      const response = await authFetch(`${BACKEND_URL}/api/auth/status?shop=${encodeURIComponent(shop)}`, {
        method: 'GET'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to check auth status');
      }
      
      setAuthStatus(data);
      
      // Notify parent component of auth status
      if (onAuthStatusChange) {
        onAuthStatusChange(data);
      }
      
    } catch (e) {
      console.error('❌ Failed to check auth status:', e);
      setError(e.message || 'Failed to check authentication status');
    } finally {
      setLoading(false);
    }
  }, [shop, onAuthStatusChange]);

  const handleReauthorize = () => {
    if (authStatus?.reauth_url) {
      
      // Set a flag to trigger plan sync after return
      sessionStorage.setItem('reauth_requested', 'true');
      sessionStorage.setItem('reauth_shop', shop);
      
      // Try multiple redirect methods for maximum compatibility
      try {
        // Method 1: Parent window redirect (for embedded apps)
        if (window.top && window.top !== window.self) {
          window.top.location.href = authStatus.reauth_url;
          return;
        }
        
        // Method 2: Direct window redirect
        window.location.href = authStatus.reauth_url;
      } catch (e) {
        console.error('❌ Redirect failed:', e);
        // Method 3: Open in new window as fallback
        window.open(authStatus.reauth_url, '_blank');
      }
    }
  };

  // Function to manually sync plan after re-authorization
  const syncPlanData = useCallback(async () => {
    if (!shop) return;
    
    try {
      
      const response = await authFetch(`${BACKEND_URL}/api/auth/sync-plan?shop=${encodeURIComponent(shop)}`, {
        method: 'POST',
        headers: {
          'x-api-key': 'HX4rA9!dj7%pQa12zLbT3V', // Use backend API key
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Plan sync failed');
      }
      
      
      // Trigger auth status recheck
      await checkAuthStatus();
      
      // Notify parent of successful sync
      if (onAuthStatusChange) {
        onAuthStatusChange({ token_valid: true, needs_reauth: false, plan_synced: true });
      }
      
      return result;
    } catch (e) {
      console.error('❌ Failed to sync plan data:', e);
      return null;
    }
  }, [shop, checkAuthStatus, onAuthStatusChange]);

  // Check for re-authorization return and trigger plan sync
  useEffect(() => {
    const handleReauthReturn = async () => {
      const reauthRequested = sessionStorage.getItem('reauth_requested');
      const reauthShop = sessionStorage.getItem('reauth_shop');
      
      if (reauthRequested === 'true' && reauthShop === shop) {
        
        // Clear the flags
        sessionStorage.removeItem('reauth_requested');
        sessionStorage.removeItem('reauth_shop');
        
        // Wait a moment for OAuth callback to complete
        setTimeout(async () => {
          await syncPlanData();
        }, 2000);
      }
    };

    handleReauthReturn();
  }, [shop, syncPlanData]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Don't render anything if auth is valid
  if (authStatus?.token_valid) {
    return null;
  }

  // Don't render during loading unless there's an error
  if (loading && !authStatus && !error) {
    return null;
  }

  // Show re-authorization prompt if needed
  if (authStatus?.needs_reauth) {
    return (
      <div className="auth-status-alert">
        <div className="alert alert-warning">
          <div className="alert-icon">🔑</div>
          <div className="alert-content">
            <h4>Re-authorization Required</h4>
            <p>
              Your Shopify app connection has expired. To see the latest plan information 
              and enable automatic synchronization, please re-authorize the app.
            </p>
            <div className="alert-actions">
              <button 
                className="btn btn-primary"
                onClick={handleReauthorize}
                disabled={!authStatus?.reauth_url}
              >
                🔄 Re-authorize App
              </button>
              <button 
                className="btn btn-secondary"
                onClick={checkAuthStatus}
                disabled={loading}
              >
                🔍 Check Again
              </button>
            </div>
            {authStatus?.error && (
              <div className="alert-error">
                <small>Error: {authStatus.error}</small>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="auth-status-error">
        <div className="alert alert-error">
          <div className="alert-icon">⚠️</div>
          <div className="alert-content">
            <h4>Authentication Check Failed</h4>
            <p>{error}</p>
            <button 
              className="btn btn-secondary"
              onClick={checkAuthStatus}
              disabled={loading}
            >
              🔄 Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}